import { useEffect, type MouseEvent, type ReactElement } from 'react';
import { backLeavesForChat, goBack, resolveBack, returnToLabel } from '../router';
import { prefetchRouteData } from '../route-data';

export interface PageBackProps {
  /**
   * The page's documented safe destination — where the trip lands when there
   * is nothing behind this entry (a cold arrival from a text message, a search
   * result, a shared link). Normally '/'.
   */
  to?: string;
  /** The route's explicit returnTo, when it carries one. Preferred over `to`. */
  returnTo?: string | null;
  /** Page-local class, for geometry and spacing only. */
  className?: string;
  /** Destination name for the accessible label when the path can't name it. */
  fallbackLabel?: string;
  /** Return true to hold the trip (a dialog takes this click). */
  onIntercept?: () => boolean;
}

/**
 * The one page-level Back control (docs/frontend-ui-standard.md §3, "Page-level
 * Back and title contract"). Every page mounts this rather than its own anchor,
 * so the anatomy, the label, and — the part that used to drift — the actual
 * navigation are all defined once.
 *
 * Back goes up one level (router.ts, resolveBackFrom): it pops the visitor's
 * own trail to the nearest page shallower in the hierarchy, however many
 * lateral hops between details that skips. It is the chevron alone (拍板
 * 2026-09-10: everyone reads the arrow; the word only crowded the row) — the
 * destination lives in the accessible name — and it can stay one control on
 * every entry path because the landing is always a page from their own trip,
 * or, with nothing usable behind this entry, the page's structural parent,
 * reached by replacing the entry rather than pushing one.
 *
 * The href is still a real path so middle-click, cmd-click, and copy-link
 * behave like a link; only the plain left-click becomes a return.
 *
 * One exception to the bare chevron (2026-10-19 拍板): when Back leaves a page
 * for the person's conversation, it says so — "Back to chat" — the same words
 * as every other way back to it (a room's pill, a read Ember, a room's door).
 * A page opened from a heads-up line in the chat is the case it was made for.
 */
export function PageBack({
  to = '/',
  returnTo,
  className = '',
  fallbackLabel,
  onIntercept,
}: PageBackProps): ReactElement {
  const target = resolveBack(returnTo, to);
  const toChat = backLeavesForChat(target);
  const label = toChat ? 'chat' : returnToLabel(target.path, fallbackLabel ?? 'Home');

  // Back is the visible, structural door to a shallower route. Warm that
  // route as soon as the door mounts so touch users get the same benefit that
  // pointer and keyboard users receive from intent prefetch below.
  useEffect(() => prefetchRouteData(target.path), [target.path]);

  const handleClick = (e: MouseEvent<HTMLAnchorElement>): void => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    if (onIntercept?.()) return;
    goBack(returnTo, to);
  };

  return (
    <a
      href={target.path}
      className={`ember-page-back${toChat ? ' is-worded' : ''}${className ? ` ${className}` : ''}`}
      aria-label={`Back to ${label}`}
      data-log="page_back"
      onClick={handleClick}
      onFocus={() => prefetchRouteData(target.path)}
      onPointerEnter={() => prefetchRouteData(target.path)}
      onTouchStart={() => prefetchRouteData(target.path)}
    >
      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="m14.5 5-7 7 7 7" />
      </svg>
      {toChat && <span aria-hidden>Back to chat</span>}
    </a>
  );
}
